import React from 'react';
import DeviceServiceForm from '../../../../components/v2/DeviceServiceForm/DeviceServiceForm';
import { useModal } from '../../../../components/v2/Modal/useModal';
import { Dir } from '../../../../modules/dirs';
import { useTranslation } from 'utils/translation';
import { useFeedbackItemInfo, useSubmitMaintenanceRequest } from '../hooks';
import { Device, MaintenanceRequestValue } from '../type';
import ServiceRequestForm from './ServiceRequestForm';
import Loader from 'components/v2/Loader';
import Section from 'components/v2/Section/Section';
import useSnackbars from '../../../../components/v2/Snackbar/use-snackbars';
import { createModalFormFooter } from '../../../../components/v2/Modal/createModalFormFooter';

export const DEVICE_PLUGIN_MAINTENANCE_REQUEST_FORM_ID =
  'plugin-device-maintenance-request';

const MaintenanceRequestModalFooter = createModalFormFooter(
  DEVICE_PLUGIN_MAINTENANCE_REQUEST_FORM_ID,
);

type Props = {
  id: Dir['id'];
  onSuccess?(): void;
  showPageTitle?: boolean;
  formId?: string;
};

const MaintenanceRequestForm = ({
  id,
  onSuccess,
  showPageTitle = true,
  formId,
}: Props) => {
  const { t } = useTranslation();
  const mutate = useSubmitMaintenanceRequest(id);
  const { data = null, isLoading } = useFeedbackItemInfo(id);

  const onSubmit = async (values: MaintenanceRequestValue) => {
    await mutate.mutateAsync({
      ...values,
      assignedUser: values.assignedUser ?? null,
      comment: values.issueDescription || '',
      title: t('maintenance_request'),
      type: t('maintenance_request'),
    });

    onSuccess?.();
  };

  if (isLoading) {
    return (
      <Section>
        <Loader />
      </Section>
    );
  }

  return (
    <DeviceServiceForm
      title={showPageTitle ? t('maintenance_request') : undefined}
      attributes={data!.fields}
      image={data?.cover}
    >
      <ServiceRequestForm
        directoryId={id}
        formId={formId}
        action={onSubmit}
        enableDescription
        enableAssignedUser
        labels={{
          description: t('service_request_note'),
        }}
      />
    </DeviceServiceForm>
  );
};

export const useMaintenanceRequest = (id: Device['id']) => {
  const { t } = useTranslation();
  const modal = useModal();
  const snackbars = useSnackbars();

  return () => {
    modal.open(
      ({ close }) => (
        <MaintenanceRequestForm
          id={id}
          formId={DEVICE_PLUGIN_MAINTENANCE_REQUEST_FORM_ID}
          showPageTitle={false}
          onSuccess={() => {
            snackbars.add({
              variant: 'success',
              message: t('maintenance_request_sent_successfully'),
            });

            close();
          }}
        />
      ),
      {
        title: t('maintenance_request'),
        footer: MaintenanceRequestModalFooter,
      },
    );
  };
};

export default MaintenanceRequestForm;
